// src/ariaSoundFx.js
// ═══════════════════════════════════════════════════════════════════════════
// Tiny Web Audio synth — no asset files needed, every sound is generated
// programmatically with oscillators + envelopes. Apple-style "tink/tock/ding"
// feedback for every interactive control in the app.
//
// Usage:
//   import { sfx } from './ariaSoundFx';
//   <button onClick={() => { sfx.click(); doThing(); }}>
//
// Sounds are short (50-300ms), low volume (5-12%), and respect the user's
// preference for reduced motion (browsers expose this; we proxy via a
// simple sessionStorage flag the user can toggle if they want silence).
// ═══════════════════════════════════════════════════════════════════════════

let ctx = null;
function getCtx() {
  if (typeof window === 'undefined') return null;
  if (!ctx) {
    try {
      const Ctor = window.AudioContext || window.webkitAudioContext;
      if (Ctor) ctx = new Ctor();
    } catch (_) { /* AudioContext blocked — sounds silently no-op */ }
  }
  // iOS/Safari suspends the context until a user gesture; resume on every
  // play attempt is safe and free if already running.
  if (ctx && ctx.state === 'suspended') {
    try { ctx.resume(); } catch (_) {}
  }
  return ctx;
}

// User can mute everything by setting localStorage.aria_sfx_muted = '1'.
function isMuted() {
  try { return localStorage.getItem('aria_sfx_muted') === '1'; }
  catch (_) { return false; }
}

/** Play one tone with attack + exponential decay envelope. */
function tone({ freq = 880, duration = 0.06, type = 'sine', volume = 0.08, attack = 0.004 }) {
  if (isMuted()) return;
  const c = getCtx();
  if (!c) return;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  const t0 = c.currentTime;
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(volume, t0 + attack);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
  osc.connect(gain).connect(c.destination);
  osc.start(t0);
  osc.stop(t0 + duration + 0.02);
}

/**
 * Play a noise-burst "click" — sounds like a real mouse/keyboard click instead
 * of a synthesized tone. Built from filtered white noise with a steep decay,
 * which is how an actual click event sounds in the physical world.
 */
function noiseClick({ duration = 0.018, volume = 0.10, freq = 2400, q = 1.4, type = 'bandpass' } = {}) {
  if (isMuted()) return;
  const c = getCtx();
  if (!c) return;
  // Build a tiny buffer of pre-shaped noise. The (1 - i/N)^2 envelope front-loads
  // the energy so it sounds like a transient, not a hiss.
  const samples = Math.max(8, Math.floor(c.sampleRate * duration));
  const buf = c.createBuffer(1, samples, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < samples; i++) {
    const env = 1 - i / samples;
    data[i] = (Math.random() * 2 - 1) * env * env;
  }
  const src = c.createBufferSource();
  src.buffer = buf;
  // Band-pass narrows the noise into a tight "tik" instead of a broad shhh.
  const bpf = c.createBiquadFilter();
  bpf.type = type;
  bpf.frequency.value = freq;
  bpf.Q.value = q;
  const gain = c.createGain();
  gain.gain.value = volume;
  src.connect(bpf).connect(gain).connect(c.destination);
  src.start();
  src.stop(c.currentTime + duration + 0.01);
}

/** Pitch-bending tone — used for the "Cool interface click tone" sweep. */
function pitchTone({ freq = 1800, pitchEnd = 1400, duration = 0.05, type = 'sine', volume = 0.06, attack = 0.001 }) {
  if (isMuted()) return;
  const c = getCtx();
  if (!c) return;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = type;
  const t0 = c.currentTime;
  osc.frequency.setValueAtTime(freq, t0);
  osc.frequency.exponentialRampToValueAtTime(pitchEnd, t0 + duration);
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(volume, t0 + attack);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
  osc.connect(gain).connect(c.destination);
  osc.start(t0);
  osc.stop(t0 + duration + 0.02);
}

/** Play a frequency arpeggio (used for the "complete" celebration sound). */
function arpeggio(notes, { gap = 70, ...toneOpts } = {}) {
  if (isMuted()) return;
  notes.forEach((freq, i) => {
    setTimeout(() => tone({ freq, ...toneOpts }), i * gap);
  });
}

// ── The actual SFX library ────────────────────────────────────────────────
export const sfx = {
  /**
   * Generic button press — TYPEWRITER SOFT CLICK (your pick #1).
   * Muted mechanical key: noise tick + low triangle thump layered together.
   * Sounds like a soft keyboard key, not a synthesized beep.
   */
  click() {
    noiseClick({ duration: 0.014, volume: 0.06, freq: 1800, q: 2.0 });
    tone({ freq: 180, duration: 0.032, type: 'triangle', volume: 0.08, attack: 0.001 });
    // Trailing edge tick — like the hammer rebound on a typewriter.
    setTimeout(() => noiseClick({ duration: 0.008, volume: 0.04, freq: 3500, q: 2.2 }), 4);
  },
  /** Toggle (Image/Video) — fuller typewriter thump. */
  toggle() {
    noiseClick({ duration: 0.018, volume: 0.07, freq: 1600, q: 1.8 });
    tone({ freq: 160, duration: 0.038, type: 'triangle', volume: 0.09, attack: 0.001 });
  },
  /**
   * Dropdown opening — COOL INTERFACE CLICK TONE (your pick #2).
   * Clean digital tone with a slight pitch sweep + papery edge transient.
   * Feels modern and crisp, like a high-end app UI.
   */
  open() {
    pitchTone({ freq: 1800, pitchEnd: 1400, duration: 0.05, type: 'sine', volume: 0.06, attack: 0.001 });
    noiseClick({ duration: 0.006, volume: 0.04, freq: 4000, q: 2.5 });
  },
  /** SIZE / micro pill — same typewriter family but lighter. */
  tap() {
    noiseClick({ duration: 0.010, volume: 0.05, freq: 2200, q: 2.0 });
    tone({ freq: 220, duration: 0.022, type: 'triangle', volume: 0.06, attack: 0.001 });
  },
  /** Generate / primary action — confident two-tone "thunk". */
  generate() {
    tone({ freq: 660, duration: 0.10, type: 'sine', volume: 0.10 });
    setTimeout(() => tone({ freq: 990, duration: 0.16, type: 'sine', volume: 0.10 }), 70);
  },
  /** Generation completed — pleasant ascending C-major arpeggio. */
  complete() {
    arpeggio([523.25, 659.25, 783.99, 1046.50], {
      duration: 0.18, type: 'sine', volume: 0.09, gap: 75,
    });
  },
  /** Soft error / cannot-do beep. */
  error() {
    tone({ freq: 220, duration: 0.18, type: 'square', volume: 0.06 });
  },
  /**
   * Arcade jump coin — classic Mario-style coin pickup, two ascending square
   * notes (B5 → E6). Plays when an image upload succeeds.
   */
  coin() {
    tone({ freq: 987.77, duration: 0.08, type: 'square', volume: 0.07, attack: 0.001 });
    setTimeout(() => tone({ freq: 1318.51, duration: 0.18, type: 'square', volume: 0.07, attack: 0.001 }), 70);
  },
  /** Mute toggle helpers (in case we add a settings UI later). */
  setMuted(m) {
    try { localStorage.setItem('aria_sfx_muted', m ? '1' : '0'); } catch (_) {}
  },
  isMuted,
};

export default sfx;
